import _ from 'lodash';
import classnames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';

import ActivateDefaultResponse from 'actions/composition/activate_default_response';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ConversationItemType from 'models/conversation_item_type';
import CreateConversation from 'actions/conversation/create_conversation';
import { getActiveChatSessionIdFromHistory } from 'actions/communication_session/session_finder';
import { getLatestManualItem, isCustomerItem } from 'scripts/application/lib/conversation_history_helpers';
import { MessageChannelType, ModerationStatus } from 'models/conversation_message';
import registerHotkey from 'components/hotkeys/register_hotkey';
import withShortcuts from 'scripts/presentation/decorators/keypress_shortcut_decorator';

export class ResponsePrompt extends React.Component {
  constructor(props) {
    super(props);

    _.bindAll(this, ['handlePromptClick']);
  }

  render() {
    return (
      <div
        className={classnames('responsePrompt', { 'responsePrompt-disabled': this.props.isDisabled })}
        data-aid="defaultResponsePrompt"
        onClick={this.handlePromptClick}
      >
        {this.getDisplayText()}
      </div>
    );
  }

  getDisplayText() {
    const { isNewConversationPending, hasConversations, customerHasActiveChat, latestCustomerItem } = this.props;

    if (isNewConversationPending) {
      return 'Creating a new conversation...';
    }

    if (!hasConversations) {
      return 'Start a conversation';
    }

    const { contentType, moderationStatus, isAutomation } = getItemAttributes(latestCustomerItem);

    if (customerHasActiveChat || (isAutomation && contentType === ConversationItemType.CHAT)) {
      return 'Reply with a chat';
    }

    if (contentType === MessageChannelType.INSTAGRAM_COMMENT) {
      if (moderationStatus === ModerationStatus.HIDDEN || moderationStatus === ModerationStatus.DELETED) {
        return 'Create a note';
      }
      return 'Reply via Instagram Comment';
    }

    return promptByContentType[contentType] ?? 'Create a note';
  }

  handlePromptClick() {
    this.respond();
  }

  respond() {
    if (this.props.isDisabled) {
      return;
    }

    if (!this.props.hasConversations) {
      this.props.onCreate();
      return;
    }

    this.props.onReply();
  }
}

const promptByContentType = Object.freeze({
  [ConversationItemType.EMAIL]: 'Reply with an email',
  [ConversationItemType.FB_MESSAGE_INCOMING]: 'Reply via Facebook',
  [ConversationItemType.VOICEMAIL]: 'Reply with a call',
  [ConversationItemType.SMS]: 'Reply with an SMS',
  [MessageChannelType.TWITTER]: 'Reply with a tweet',
  [MessageChannelType.WHATSAPP]: 'Reply with a WhatsApp',
  [MessageChannelType.INSTAGRAM_DIRECT]: 'Reply via Instagram',
  // Instagram Comment handled separately due to moderation
});

function getItemAttributes(item) {
  if (!item) {
    return { contentType: undefined, moderationStatus: undefined, isAutomation: false };
  }

  const type = item.contentType();
  const content = item.content;
  const isAutomation = type === ConversationItemType.MESSAGE_AUTOMATION_SUMMARY;

  let contentType = type;
  if (type === ConversationItemType.CONVERSATION_MESSAGE) {
    contentType = content?.channel;
  } else if (isAutomation) {
    contentType = content?.interactionType;
  }

  const moderationStatus = content?.meta?.moderationStatus;
  return { contentType, moderationStatus, isAutomation };
}

export const ResponsePromptWithShortcutHandler = withShortcuts(ResponsePrompt, [
  registerHotkey({
    key: 'alt+r',
    callback: instance => instance.respond(),
    group: 'Conversation',
    label: 'Reply to or reopen conversation',
  }),
]);

ResponsePrompt.propTypes = ResponsePromptWithShortcutHandler.propTypes = {
  hasConversations: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isNewConversationPending: PropTypes.bool,
  latestCustomerItem: PropTypes.instanceOf(ConversationItem),
  onCreate: PropTypes.func.isRequired,
  onReply: PropTypes.func.isRequired,
};

const ResponsePromptContainer = connect(mapStateToProps, mapExecuteToProps)(ResponsePromptWithShortcutHandler);
export default ResponsePromptContainer;

ResponsePromptContainer.propTypes = {
  conversationId: PropTypes.string,
};

function mapStateToProps({ getProvider }, { conversationId }) {
  const profile = getProvider('profile').get();
  let conversationHistory = getProvider('conversationHistory');
  let conversations = getProvider('conversations');

  let latestCustomerItem = getLatestManualItem({
    conversationHistory,
    conversationId,
    filter: isCustomerItem,
  });

  let isNewConversationPending = getProvider('conversations').isPendingNew();
  let currentItemsLoading = getProvider('conversationHistory').isLoading();
  const isNavigatingToNextConversation = getProvider('conversationWorkflow').get().isNavigatingNextConversation;

  return {
    customerHasActiveChat: !!getActiveChatSessionIdFromHistory({ conversationHistory, conversations }),
    hasConversations: conversations.count() > 0,
    isDisabled:
      !(profile && profile.id) || isNewConversationPending || currentItemsLoading || isNavigatingToNextConversation,
    isNewConversationPending,
    latestCustomerItem,
  };
}

function mapExecuteToProps(executeAction, { conversationId }) {
  return {
    onCreate: () => executeAction(CreateConversation),
    onReply: () => executeAction(ActivateDefaultResponse, { conversationId }),
  };
}
